import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from './UserProfileContext';

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  role: 'owner' | 'admin' | 'member';
  app_role: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  organizationId: string | null;
  isLoading: boolean;
  error: Error | null;
}

const OrganizationContext = createContext<OrganizationContextType>({
  currentOrganization: null,
  organizations: [],
  organizationId: null,
  isLoading: true,
  error: null,
});

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUserProfile();

  const { data: organizations = [], isLoading, error } = useQuery({
    queryKey: ['user-organizations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          role,
          app_role,
          organizations:organization_id (
            id,
            name,
            slug,
            logo_url
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      return (data || []).map((member: any) => ({
        id: member.organizations.id,
        name: member.organizations.name,
        slug: member.organizations.slug,
        logo_url: member.organizations.logo_url,
        role: member.role,
        app_role: member.app_role,
      })) as Organization[];
    },
    enabled: !!user?.id,
  });

  const currentOrganization = useMemo(() => {
    // Return the first organization (could be enhanced to support org switching)
    return organizations[0] || null;
  }, [organizations]);

  const organizationId = currentOrganization?.id || null;

  const value = useMemo(() => ({
    currentOrganization,
    organizations,
    organizationId,
    isLoading,
    error: error as Error | null,
  }), [currentOrganization, organizations, organizationId, isLoading, error]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}
